'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useChat } from '@/lib/hooks/useChat';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isPdf?: boolean;
  responseTime?: number;
  tokenCount?: number;
  hasError?: boolean;
}

interface ChatSession {
  id: string;
  title: string;
  messages: Message[];
  createdAt: Date;
  updatedAt: Date;
  messageCount: number;
}

interface ClaudeChatWidgetProps {
  context?: {
    page: 'flight-search' | 'booking' | 'booking-management';
    searchParams?: any;
    bookingData?: any;
  };
}

interface ApiStatus {
  status: 'operational' | 'degraded' | 'down' | 'checking';
  latency: number | null;
  lastChecked: Date | null;
  errorCount: number;
  successCount: number;
  uptime: number;
}

const SYSTEM_PROMPT = `You are the DIAL FOR FLIGHT AI Travel Assistant — a professional, highly accurate travel expert with 98.5% accuracy across all travel queries. You are powered by advanced AI and serve UK-based customers.

Your capabilities include:
1. **Flight Search & Booking**: Find best flights, compare prices, suggest optimal routes and airlines
2. **Hotel Recommendations**: Suggest hotels based on budget, location, and preferences
3. **Holiday Packages**: Create complete holiday packages with flights + hotels
4. **Visa Requirements**: Provide visa information for UK passport holders
5. **Price Monitoring**: Explain price trends and best booking windows
6. **Itinerary Planning**: Create detailed day-by-day travel itineraries
7. **PDF Itinerary Generation**: When asked, generate a professional text-based itinerary that can be saved as PDF
8. **Travel Documents**: Advise on required travel documents, insurance, and health requirements
9. **PNR Tracking**: Help customers understand their 6-character booking references (e.g., AB3K7X)
10. **Budget Planning**: Help plan trips within specific GBP budgets

Contact Information for DIAL FOR FLIGHT:
- Phone: 020 4615 2876 (24/7)
- Email: info@dialforflight.co.uk
- WhatsApp: 020 4615 2876
- Address: 36 Santiago Street, Manchester, M14 4BL, UK
- Company Number: 17005493

When generating PDF itineraries, format them clearly with:
- Trip title and dates
- Day-by-day breakdown
- Flight details with PNR references
- Hotel information
- Activities and recommendations
- Emergency contacts and important numbers
- Total cost breakdown in GBP

Always respond in a professional, helpful manner. Provide specific GBP prices when possible. Mention that customers can call 020 4615 2876 or email info@dialforflight.co.uk for immediate human assistance.`;

function getErrorMessage(error: Error | null): { title: string; message: string; action: string; icon: string } {
  if (!error) return { title: '', message: '', action: '', icon: '' };
  const msg = error.message?.toLowerCase() || '';

  if (msg.includes('429') || msg.includes('rate limit') || msg.includes('too many requests')) {
    return {
      title: 'Rate Limit Reached',
      message: 'Too many requests. Please wait a moment before trying again.',
      action: 'Wait 30 seconds and retry',
      icon: 'ClockIcon',
    };
  }
  if (msg.includes('401') || msg.includes('authentication') || msg.includes('invalid api key') || msg.includes('credit balance')) {
    return {
      title: 'Authentication Error',
      message: 'API key issue detected. Our team has been notified.',
      action: 'Call us: 020 4615 2876',
      icon: 'KeyIcon',
    };
  }
  if (msg.includes('403') || msg.includes('permission') || msg.includes('forbidden')) {
    return {
      title: 'Access Restricted',
      message: 'This feature is temporarily restricted. Please contact support.',
      action: 'Contact support',
      icon: 'ShieldExclamationIcon',
    };
  }
  if (msg.includes('500') || msg.includes('502') || msg.includes('503') || msg.includes('server error') || msg.includes('unavailable')) {
    return {
      title: 'Service Unavailable',
      message: 'Anthropic servers are temporarily down. Please try again shortly.',
      action: 'Retry in a few minutes',
      icon: 'ServerIcon',
    };
  }
  if (msg.includes('529') || msg.includes('overloaded')) {
    return {
      title: 'AI Overloaded',
      message: 'High demand right now. Your request will be processed shortly.',
      action: 'Retry in 1-2 minutes',
      icon: 'CpuChipIcon',
    };
  }
  if (msg.includes('network') || msg.includes('fetch') || msg.includes('failed to fetch') || msg.includes('connection')) {
    return {
      title: 'Network Error',
      message: 'Connection lost. Please check your internet and try again.',
      action: 'Check connection & retry',
      icon: 'WifiIcon',
    };
  }
  if (msg.includes('timeout') || msg.includes('timed out')) {
    return {
      title: 'Request Timeout',
      message: 'The AI took too long to respond. Please try a shorter message.',
      action: 'Try a shorter message',
      icon: 'ClockIcon',
    };
  }
  return {
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred. Please try again.',
    action: 'Retry your message',
    icon: 'ExclamationTriangleIcon',
  };
}

export default function ClaudeChatWidget({ context }: ClaudeChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showStatusPanel, setShowStatusPanel] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [streamingTokens, setStreamingTokens] = useState(0);
  const [streamStartTime, setStreamStartTime] = useState<number | null>(null);
  const [apiStatus, setApiStatus] = useState<ApiStatus>({
    status: 'checking',
    latency: null,
    lastChecked: null,
    errorCount: 0,
    successCount: 0,
    uptime: 100,
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const requestStartRef = useRef<number | null>(null);

  const { response, fullResponse, isLoading, error, sendMessage } = useChat(
    'ANTHROPIC',
    'claude-sonnet-4-5-20250929',
    true
  );

  // Check API status on mount and periodically
  const checkApiStatus = useCallback(async () => {
    setApiStatus(prev => ({ ...prev, status: 'checking' }));
    const start = Date.now();
    try {
      const res = await fetch('/api/ai/chat-completion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: 'ANTHROPIC',
          model: 'claude-sonnet-4-5-20250929',
          messages: [{ role: 'user', content: 'ping' }],
          parameters: { max_tokens: 5 },
        }),
        signal: AbortSignal.timeout(8000),
      });
      const latency = Date.now() - start;
      if (res.ok) {
        setApiStatus(prev => ({
          ...prev,
          status: latency > 3000 ? 'degraded' : 'operational',
          latency,
          lastChecked: new Date(),
          successCount: prev.successCount + 1,
          uptime: Math.min(100, ((prev.successCount + 1) / (prev.successCount + prev.errorCount + 1)) * 100),
        }));
      } else {
        throw new Error(`HTTP ${res.status}`);
      }
    } catch {
      setApiStatus(prev => ({
        ...prev,
        status: 'down',
        latency: null,
        lastChecked: new Date(),
        errorCount: prev.errorCount + 1,
        uptime: (prev.successCount / (prev.successCount + prev.errorCount + 1)) * 100,
      }));
    }
  }, []);

  useEffect(() => {
    checkApiStatus();
    const interval = setInterval(checkApiStatus, 60000);
    return () => clearInterval(interval);
  }, [checkApiStatus]);

  // Load sessions from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('dff_chat_sessions');
      if (stored) {
        const parsed = JSON.parse(stored) as any[];
        const sessions: ChatSession[] = parsed.map(s => ({
          ...s,
          createdAt: new Date(s.createdAt),
          updatedAt: new Date(s.updatedAt),
          messages: s.messages.map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) })),
        }));
        setChatSessions(sessions);
      }
    } catch {}
  }, []);

  const saveSessionToStorage = (sessions: ChatSession[]) => {
    try {
      localStorage.setItem('dff_chat_sessions', JSON.stringify(sessions));
    } catch {}
  };

  const saveCurrentSession = useCallback((msgs: Message[]) => {
    const realMessages = msgs.filter(m => m.id !== 'welcome' && m.id !== 'streaming' && !m.hasError);
    if (realMessages.length === 0) return;
    const userMessages = realMessages.filter(m => m.role === 'user');
    if (userMessages.length === 0) return;
    const title = userMessages[0].content.slice(0, 50) + (userMessages[0].content.length > 50 ? '...' : '');
    const now = new Date();
    setChatSessions(prev => {
      let updated: ChatSession[];
      if (currentSessionId) {
        updated = prev.map(s => s.id === currentSessionId
          ? { ...s, messages: msgs, updatedAt: now, messageCount: realMessages.length, title }
          : s
        );
      } else {
        const newSession: ChatSession = {
          id: `session_${Date.now()}`,
          title,
          messages: msgs,
          createdAt: now,
          updatedAt: now,
          messageCount: realMessages.length,
        };
        setCurrentSessionId(newSession.id);
        updated = [newSession, ...prev].slice(0, 20); // keep last 20 sessions
      }
      saveSessionToStorage(updated);
      return updated;
    });
  }, [currentSessionId]);

  const loadSession = (session: ChatSession) => {
    setMessages(session.messages);
    setCurrentSessionId(session.id);
    setShowHistoryPanel(false);
  };

  const deleteSession = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setChatSessions(prev => {
      let updated = prev.filter(s => s.id !== sessionId);
      saveSessionToStorage(updated);
      return updated;
    });
    if (currentSessionId === sessionId) setCurrentSessionId(null);
  };

  const startNewSession = () => {
    if (messages.length > 1) saveCurrentSession(messages);
    setCurrentSessionId(null);
    const welcomeMessage: Message = {
      id: 'welcome',
      role: 'assistant',
      content: getWelcomeMessage(),
      timestamp: new Date(),
    };
    setMessages([welcomeMessage]);
    setShowHistoryPanel(false);
  };

  // Enhanced error handling with user-friendly messages
  useEffect(() => {
    if (error) {
      const errInfo = getErrorMessage(error);
      toast.error(`${errInfo.title}: ${errInfo.message}`, { duration: 5000 });
      setApiStatus(prev => ({
        ...prev,
        status: 'degraded',
        errorCount: prev.errorCount + 1,
        uptime: (prev.successCount / (prev.successCount + prev.errorCount + 1)) * 100,
      }));
      // Add error message to chat
      const errMsg: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `⚠️ **${errInfo.title}**\n\n${errInfo.message}\n\n💡 **Tip:** ${errInfo.action}\n\n📞 Need help? Call us at **020 4615 2876** or email **info@dialforflight.co.uk**`,
        timestamp: new Date(),
        hasError: true,
      };
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== 'streaming');
        return [...filtered, errMsg];
      });
    }
  }, [error]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, response]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  // Track streaming tokens
  useEffect(() => {
    if (isLoading && response) {
      const approxTokens = Math.floor(response.length / 4);
      setStreamingTokens(approxTokens);
    }
    if (!isLoading) {
      setStreamingTokens(0);
      setStreamStartTime(null);
    }
  }, [isLoading, response]);

  const getWelcomeMessage = useCallback(() => {
    if (context?.page === 'flight-search') {
      return "Hi! I'm your **DIAL FOR FLIGHT AI Assistant** with **98.5% accuracy** across all travel queries. I can help you find the perfect flight, compare prices, generate PDF itineraries, and much more. What would you like to know?";
    } else if (context?.page === 'booking') {
      return "Hello! I'm your **DIAL FOR FLIGHT AI Assistant**. I can help you complete your booking, generate a professional PDF itinerary, or answer any travel questions. How can I assist?";
    } else if (context?.page === 'booking-management') {
      return "Welcome! I can help you manage your bookings, track your 6-character PNR reference, generate PDF documents, or assist with modifications. How can I help today?";
    }
    return "Hi! I'm your **DIAL FOR FLIGHT AI Travel Assistant** — powered by advanced AI with **98.5% accuracy**. I can help with:\n\n✈️ Flight search & booking\n🏨 Hotel recommendations\n📋 PDF itinerary generation\n🌍 Visa requirements\n💷 GBP price comparisons\n📞 Connect you with our team: **020 4615 2876**\n\nWhat can I help you with today?";
  }, [context?.page]);

  useEffect(() => {
    if (messages.length === 0) {
      const welcomeMessage: Message = {
        id: 'welcome',
        role: 'assistant',
        content: getWelcomeMessage(),
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getWelcomeMessage]);

  // Track when streaming response completes
  const prevLoadingRef = useRef(false);
  useEffect(() => {
    if (prevLoadingRef.current && !isLoading && response && !error) {
      const responseTime = requestStartRef.current ? Date.now() - requestStartRef.current : null;
      const tokenCount = Math.floor(response.length / 4);
      const assistantMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: response,
        timestamp: new Date(),
        responseTime: responseTime ?? undefined,
        tokenCount,
      };
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== 'streaming');
        let updated = [...filtered, assistantMessage];
        // Auto-save session after each AI response
        setTimeout(() => saveCurrentSession(updated), 100);
        return updated;
      });
      // Update API status on success
      if (responseTime) {
        setApiStatus(prev => ({
          ...prev,
          status: responseTime > 5000 ? 'degraded' : 'operational',
          latency: responseTime,
          lastChecked: new Date(),
          successCount: prev.successCount + 1,
          uptime: Math.min(100, ((prev.successCount + 1) / (prev.successCount + prev.errorCount + 1)) * 100),
        }));
      }
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading, response, error, saveCurrentSession]);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
    });
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage + (attachedFile ? ` [Attached: ${attachedFile.name}]` : ''),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputMessage;
    setInputMessage('');
    requestStartRef.current = Date.now();
    setStreamStartTime(Date.now());
    setStreamingTokens(0);

    const conversationMessages: any[] = [
      ...messages
        .filter(m => m.id !== 'welcome' && m.id !== 'streaming' && !m.hasError)
        .map(m => ({ role: m.role, content: m.content })),
    ];

    if (attachedFile) {
      const base64Data = await fileToBase64(attachedFile);
      conversationMessages.push({
        role: 'user',
        content: [
          { type: 'text', text: currentInput },
          {
            type: 'file',
            file: {
              file_data: base64Data,
              filename: attachedFile.name,
              format: attachedFile.type,
            },
          },
        ],
      });
      setAttachedFile(null);
    } else {
      conversationMessages.push({ role: 'user', content: currentInput });
    }

    sendMessage(conversationMessages, { system: SYSTEM_PROMPT, temperature: 0.7, max_tokens: 2048 });
  };

  const handleGeneratePdf = async () => {
    if (isLoading) return;
    setIsGeneratingPdf(true);

    const pdfRequestMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: 'Please generate a professional PDF itinerary based on our conversation. Include all flight details, hotel information, day-by-day activities, cost breakdown in GBP, and emergency contact numbers.',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, pdfRequestMessage]);
    requestStartRef.current = Date.now();

    const pdfSystemPrompt = SYSTEM_PROMPT + '\n\nIMPORTANT: The user wants a PDF itinerary. Generate a comprehensive, professionally formatted itinerary with clear sections, all details, and a complete cost breakdown. Format it so it can be easily printed or saved as PDF.';

    const conversationMessages: any[] = [
      ...messages
        .filter(m => m.id !== 'welcome' && m.id !== 'streaming' && !m.hasError)
        .map(m => ({ role: m.role, content: m.content })),
      { role: 'user', content: 'Generate a professional PDF itinerary from our conversation.' },
    ];

    sendMessage(conversationMessages, { system: pdfSystemPrompt, temperature: 0.3, max_tokens: 4096 });
    setIsGeneratingPdf(false);
  };

  const handleDownloadPdf = () => {
    const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant' && m.id !== 'welcome' && !m.hasError);
    if (!lastAssistantMessage) {
      toast.error('No itinerary to download. Ask the AI to generate one first.');
      return;
    }

    const content = `DIAL FOR FLIGHT - Professional Travel Itinerary\n${'='.repeat(50)}\nGenerated: ${new Date().toLocaleDateString('en-GB')}\nContact: 020 4615 2876 | info@dialforflight.co.uk\n${'='.repeat(50)}\n\n${lastAssistantMessage.content}`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `DialForFlight-Itinerary-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Itinerary downloaded! Open in Word or Google Docs to save as PDF.');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    if (messages.length > 1) saveCurrentSession(messages);
    setCurrentSessionId(null);
    const welcomeMessage: Message = {
      id: 'welcome',
      role: 'assistant',
      content: getWelcomeMessage(),
      timestamp: new Date(),
    };
    setMessages([welcomeMessage]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain'];
    if (!validTypes.includes(file.type)) {
      toast.error('Supported: JPEG, PNG, GIF, WebP, PDF, TXT');
      return;
    }
    setAttachedFile(file);
    toast.success(`File attached: ${file.name}`);
  };

  const quickActions = [
    { label: '✈️ Find Flights', message: 'Help me find the best flights from London' },
    { label: '📋 Generate PDF', message: 'Generate a professional PDF itinerary for my trip' },
    { label: '🌍 Visa Info', message: 'What visa do I need as a UK passport holder?' },
    { label: '💷 Best Deals', message: 'What are the best flight deals available right now?' },
  ];

  const handleHistoryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowHistoryPanel(!showHistoryPanel);
    setShowStatusPanel(false);
  };

  const statusColor = {
    operational: 'bg-green-500',
    degraded: 'bg-yellow-500',
    down: 'bg-red-500',
    checking: 'bg-gray-400',
  }[apiStatus.status];

  const statusLabel = {
    operational: 'Operational',
    degraded: 'Degraded',
    down: 'Down',
    checking: 'Checking...',
  }[apiStatus.status];

  const streamingDuration = streamStartTime ? Math.floor((Date.now() - streamStartTime) / 1000) : 0;

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 left-6 flex items-center gap-2 bg-gradient-to-br from-primary to-secondary text-white pl-4 pr-5 py-3 rounded-full shadow-2xl hover:shadow-primary/50 hover:scale-105 transition-all duration-300 z-50 group"
          aria-label="Open AI Travel Assistant"
        >
          <div className="relative">
            <Icon name="SparklesIcon" size={22} className="group-hover:scale-110 transition-transform" />
            <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-trust-green opacity-75"></span>
              <span className="relative inline-flex rounded-full h-4 w-4 bg-trust-green items-center justify-center">
                <span className="text-[7px] text-white font-black">AI</span>
              </span>
            </span>
          </div>
          <div className="text-left">
            <p className="text-xs text-white/80 leading-none">AI Assistant</p>
            <p className="text-sm font-black leading-tight">Chat Now</p>
          </div>
          <span className="absolute inset-0 rounded-full animate-ping opacity-20 bg-primary"></span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`fixed bottom-6 right-6 w-96 bg-white rounded-2xl shadow-2xl flex flex-col z-50 border-2 border-primary/20 transition-all duration-300 ${isMinimized ? 'h-16' : 'h-[640px]'}`}>
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-secondary text-white p-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Icon name="SparklesIcon" size={20} />
              </div>
              <div>
                <h3 className="font-black text-base">DIAL FOR FLIGHT AI</h3>
                <div className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${statusColor} ${apiStatus.status === 'operational' ? 'animate-pulse' : ''}`}></span>
                  <p className="text-xs text-white/90">98.5% Accuracy • {statusLabel}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* History Toggle */}
              <button
                onClick={handleHistoryClick}
                className={`p-2 hover:bg-white/20 rounded-lg transition-colors ${showHistoryPanel ? 'bg-white/20' : ''}`}
                aria-label="Chat History"
                title="Chat History"
              >
                <Icon name="ClockIcon" size={18} />
              </button>
              {/* API Status Toggle */}
              <button
                onClick={() => { setShowStatusPanel(!showStatusPanel); setShowHistoryPanel(false); }}
                className={`p-2 hover:bg-white/20 rounded-lg transition-colors ${showStatusPanel ? 'bg-white/20' : ''}`}
                aria-label="API Status"
                title="API Status Monitor"
              >
                <Icon name="SignalIcon" size={18} />
              </button>
              <button
                onClick={handleGeneratePdf}
                disabled={isLoading || messages.length <= 1}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-40"
                aria-label="Generate PDF itinerary"
                title="Generate PDF Itinerary"
              >
                <Icon name="DocumentArrowDownIcon" size={18} />
              </button>
              <button
                onClick={handleDownloadPdf}
                disabled={messages.length <= 1}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-40"
                aria-label="Download itinerary"
                title="Download Itinerary"
              >
                <Icon name="ArrowDownTrayIcon" size={18} />
              </button>
              <button
                onClick={clearChat}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="New chat"
                title="New Chat"
              >
                <Icon name="ArrowPathIcon" size={18} />
              </button>
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Minimize chat"
              >
                <Icon name={isMinimized ? 'ChevronUpIcon' : 'MinusIcon'} size={18} />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Close chat"
              >
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* History Panel */}
              {showHistoryPanel && (
                <div className="bg-gray-900 text-white flex-shrink-0 border-b border-gray-700 max-h-64 overflow-y-auto">
                  <div className="flex items-center justify-between px-4 py-2.5 sticky top-0 bg-gray-900 border-b border-gray-800">
                    <span className="text-xs font-bold text-gray-300 uppercase tracking-wider">Chat History</span>
                    <button
                      onClick={startNewSession}
                      className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      <Icon name="PlusIcon" size={12} />
                      New Chat
                    </button>
                  </div>
                  {chatSessions.length === 0 ? (
                    <div className="px-4 py-6 text-center">
                      <Icon name="ClockIcon" size={24} className="text-gray-600 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">No previous conversations</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-800">
                      {chatSessions.map(session => (
                        <div
                          key={session.id}
                          onClick={() => loadSession(session)}
                          className={`flex items-start justify-between px-4 py-3 hover:bg-gray-800 cursor-pointer transition-colors ${
                            currentSessionId === session.id ? 'bg-gray-800 border-l-2 border-blue-500' : ''
                          }`}
                        >
                          <div className="flex-1 min-w-0 mr-2">
                            <p className="text-xs font-bold text-gray-200 truncate">{session.title}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[10px] text-gray-500">
                                {session.updatedAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} • {session.updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              <span className="text-[10px] text-gray-600">{session.messageCount} msgs</span>
                            </div>
                          </div>
                          <button
                            onClick={e => deleteSession(session.id, e)}
                            className="text-gray-600 hover:text-red-400 transition-colors flex-shrink-0 p-0.5"
                          >
                            <Icon name="XMarkIcon" size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* API Status Panel */}
              {showStatusPanel && (
                <div className="bg-gray-900 text-white px-4 py-3 flex-shrink-0 border-b border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-gray-300 uppercase tracking-wider">Anthropic API Status</span>
                    <button
                      onClick={checkApiStatus}
                      className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors"
                    >
                      <Icon name="ArrowPathIcon" size={12} />
                      Refresh
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-gray-800 rounded-lg p-2 text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <span className={`w-2 h-2 rounded-full ${statusColor}`}></span>
                        <span className={`text-xs font-bold ${
                          apiStatus.status === 'operational' ? 'text-green-400' :
                          apiStatus.status === 'degraded' ? 'text-yellow-400' :
                          apiStatus.status === 'down' ? 'text-red-400' : 'text-gray-400'
                        }`}>{statusLabel}</span>
                      </div>
                      <p className="text-[10px] text-gray-500">API Health</p>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-2 text-center">
                      <p className={`text-xs font-bold ${
                        apiStatus.latency === null ? 'text-gray-400' :
                        apiStatus.latency < 1000 ? 'text-green-400' :
                        apiStatus.latency < 3000 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {apiStatus.latency !== null ? `${apiStatus.latency}ms` : '—'}
                      </p>
                      <p className="text-[10px] text-gray-500">Latency</p>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-2 text-center">
                      <p className={`text-xs font-bold ${
                        apiStatus.uptime >= 99 ? 'text-green-400' :
                        apiStatus.uptime >= 95 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {apiStatus.uptime.toFixed(0)}%
                      </p>
                      <p className="text-[10px] text-gray-500">Uptime</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-gray-500">✓ {apiStatus.successCount} success</span>
                      <span className="text-[10px] text-gray-500">✗ {apiStatus.errorCount} errors</span>
                    </div>
                    {apiStatus.lastChecked && (
                      <span className="text-[10px] text-gray-600">
                        Checked {apiStatus.lastChecked.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  {/* Latency bar */}
                  {apiStatus.latency !== null && (
                    <div className="mt-2">
                      <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                        <span>Response Time</span>
                        <span>{apiStatus.latency < 1000 ? 'Fast' : apiStatus.latency < 3000 ? 'Normal' : 'Slow'}</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all duration-500 ${
                            apiStatus.latency < 1000 ? 'bg-green-500' :
                            apiStatus.latency < 3000 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(100, (apiStatus.latency / 5000) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Accuracy Badge */}
              <div className="bg-trust-green/10 border-b border-trust-green/20 px-4 py-2 flex items-center gap-2 flex-shrink-0">
                <Icon name="ShieldCheckIcon" size={14} className="text-trust-green" />
                <span className="text-xs font-bold text-trust-green">AI-Powered • 98.5% Accuracy • PDF Generation • Multi-language Support</span>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {message.role === 'assistant' && (
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mr-2 mt-1 ${
                        message.hasError ? 'bg-red-100' : 'bg-gradient-to-br from-primary to-secondary'
                      }`}>
                        <Icon
                          name={message.hasError ? 'ExclamationTriangleIcon' : 'SparklesIcon'}
                          size={14}
                          className={message.hasError ? 'text-red-500' : 'text-white'}
                        />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        message.role === 'user' ?'bg-gradient-to-br from-primary to-secondary text-white'
                          : message.hasError
                          ? 'bg-red-50 border border-red-200 text-gray-900' :'bg-white border border-gray-200 text-gray-900'
                      }`}
                    >
                      {message.role === 'assistant' ? (
                        <div className="text-sm prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-800 prose-strong:text-gray-900">
                          <ReactMarkdown>{message.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      )}
                      <div className={`flex items-center justify-between mt-1 gap-2`}>
                        <p className={`text-xs ${
                          message.role === 'user' ? 'text-white/70' : 'text-gray-400'
                        }`}>
                          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        {message.responseTime && (
                          <span className="text-[10px] text-gray-400">
                            {message.responseTime < 1000
                              ? `${message.responseTime}ms`
                              : `${(message.responseTime / 1000).toFixed(1)}s`}
                          </span>
                        )}
                        {message.tokenCount && (
                          <span className="text-[10px] text-gray-400">~{message.tokenCount} tokens</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Live Streaming Response */}
                {isLoading && response && (
                  <div className="flex justify-start">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0 mr-2 mt-1">
                      <Icon name="SparklesIcon" size={14} className="text-white" />
                    </div>
                    <div className="max-w-[80%] bg-white border border-primary/30 rounded-2xl px-4 py-3 shadow-sm">
                      {/* Streaming header */}
                      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
                        <div className="flex gap-0.5">
                          <span className="w-1.5 h-3 bg-primary rounded-full animate-[bounce_0.8s_ease-in-out_infinite]" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-1.5 h-3 bg-primary/70 rounded-full animate-[bounce_0.8s_ease-in-out_infinite]" style={{ animationDelay: '120ms' }}></span>
                          <span className="w-1.5 h-3 bg-primary/40 rounded-full animate-[bounce_0.8s_ease-in-out_infinite]" style={{ animationDelay: '240ms' }}></span>
                        </div>
                        <span className="text-[10px] text-primary font-bold uppercase tracking-wider">Streaming</span>
                        <span className="text-[10px] text-gray-400 ml-auto">~{streamingTokens} tokens</span>
                      </div>
                      <div className="text-sm prose prose-sm max-w-none">
                        <ReactMarkdown>{response}</ReactMarkdown>
                      </div>
                      {/* Blinking cursor */}
                      <span className="inline-block w-0.5 h-4 bg-primary animate-pulse ml-0.5 align-middle"></span>
                    </div>
                  </div>
                )}

                {/* Typing indicator (before first token) */}
                {isLoading && !response && (
                  <div className="flex justify-start">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0 mr-2">
                      <Icon name="SparklesIcon" size={14} className="text-white" />
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                        <p className="text-xs text-gray-500">AI is thinking...</p>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Streaming Status Bar */}
              {isLoading && (
                <div className="px-4 py-1.5 bg-primary/5 border-t border-primary/10 flex items-center gap-2 flex-shrink-0">
                  <div className="flex gap-0.5 items-center">
                    <span className="w-1 h-1 bg-primary rounded-full animate-ping"></span>
                  </div>
                  <span className="text-[10px] text-primary font-bold">
                    {response ? `Streaming response • ~${streamingTokens} tokens` : 'Connecting to AI...'}
                  </span>
                  {streamStartTime && response && (
                    <span className="text-[10px] text-gray-400 ml-auto">{streamingDuration}s</span>
                  )}
                </div>
              )}

              {/* Quick Actions */}
              {messages.length <= 1 && (
                <div className="px-4 py-2 border-t border-gray-100 bg-white flex-shrink-0">
                  <p className="text-xs text-gray-500 mb-2 font-bold">Quick Actions:</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {quickActions.map((action, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setInputMessage(action.message);
                          inputRef.current?.focus();
                        }}
                        className="text-xs px-2 py-1.5 bg-primary/5 hover:bg-primary/10 text-primary font-bold rounded-lg transition-colors text-left"
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* File attachment preview */}
              {attachedFile && (
                <div className="px-4 py-2 bg-primary/5 border-t border-primary/10 flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <Icon name="PaperClipIcon" size={14} className="text-primary" />
                    <span className="text-xs text-primary font-bold truncate max-w-[200px]">{attachedFile.name}</span>
                  </div>
                  <button onClick={() => setAttachedFile(null)} className="text-gray-400 hover:text-red-500 transition-colors">
                    <Icon name="XMarkIcon" size={14} />
                  </button>
                </div>
              )}

              {/* Input */}
              <div className="p-4 border-t border-gray-200 bg-white rounded-b-2xl flex-shrink-0">
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                    className="p-2 text-gray-400 hover:text-primary transition-colors disabled:opacity-50"
                    title="Attach file (PDF, image, or document)"
                  >
                    <Icon name="PaperClipIcon" size={20} />
                  </button>
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={isLoading ? 'AI is responding...' : 'Ask about flights, hotels, visas, PDF...'}
                    disabled={isLoading}
                    className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-50 text-sm"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim() || isLoading}
                    className="p-2.5 bg-gradient-to-br from-primary to-secondary text-white rounded-xl hover:shadow-lg transition-all duration-300 disabled:opacity-50 hover:scale-105"
                    aria-label="Send message"
                  >
                    <Icon name="PaperAirplaneIcon" size={18} />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-[10px] text-gray-400">📞 020 4615 2876 | ✉️ info@dialforflight.co.uk</p>
                  <div className="flex items-center gap-1">
                    <span className={`w-1.5 h-1.5 rounded-full ${statusColor}`}></span>
                    <p className="text-[10px] text-gray-400">{statusLabel}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
